import { useState, useMemo } from 'react';
import { X, User, Briefcase, FileText, Check, Loader2, ArrowLeft, ArrowRight, MapPin } from 'lucide-react';
import { useTranslation } from '@crew-at-sea/i18n';
import { commonApi } from '@crew-at-sea/api-client';
import toast from 'react-hot-toast';
import { getCountries } from '@/utils';
import { useRanks } from '@/hooks';
import { SearchableSelect } from '@/components/SearchableSelect';

interface CvWizardProps {
  open: boolean;
  onClose: () => void;
  profile: any;
  onUpdate: (data: any) => Promise<any>;
}

export function CvWizard({ open, onClose, profile, onUpdate }: CvWizardProps) {
  const { t, i18n } = useTranslation(['panel', 'common', 'enums']);
  const countries = useMemo(() => getCountries(i18n.language), [i18n.language]);
  const { data: ranksResponse } = useRanks();
  const ranks = ranksResponse?.data || ranksResponse || [];
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    nationality: profile?.nationality || '',
    dateOfBirth: profile?.dateOfBirth ? new Date(profile.dateOfBirth).toISOString().split('T')[0] : '',
    gender: profile?.gender || '',
    phone: profile?.phone || '',
    country: profile?.country || '',
    city: profile?.city || '',
    rankId: profile?.rankId || profile?.rank?.id || '',
    department: profile?.department || '',
    experienceYears: profile?.experienceYears || 0,
    bio: profile?.bio || '',
    cvPdfUrl: profile?.cvPdfUrl || '',
  });

  if (!open) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'experienceYears' ? parseInt(value) || 0 : value,
    }));
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error(t('common:errors.validation.invalidFileType', { defaultValue: 'Lütfen sadece PDF dosyası seçin.' }));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('documents.fileTooLarge', { defaultValue: 'Dosya boyutu 10MB\'dan küçük olmalıdır.' }));
        return;
      }
      setUploadingPdf(true);
      try {
        const uploadResult = await commonApi.uploadDocument(file);
        const fileUrl = uploadResult?.url || (uploadResult as any)?.data?.url;
        if (fileUrl) {
          setFormData((prev) => ({ ...prev, cvPdfUrl: fileUrl }));
          toast.success(t('profile.toast.pdfUploaded'));
        }
      } catch (err) {
        console.error('PDF upload error:', err);
        toast.error(t('profile.toast.pdfUploadError', { defaultValue: 'PDF yüklenirken hata oluştu.' }));
      } finally {
        setUploadingPdf(false);
      }
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString() : undefined,
      };
      await onUpdate(payload);
      toast.success(t('profile.toast.updateSuccess'));
      onClose();
    } catch (err) {
      toast.error(t('profile.toast.updateError'));
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { id: 1, name: t('profile.personalInfo', { defaultValue: 'Kişisel Bilgiler' }), icon: User },
    { id: 2, name: t('profile.contactInfo', { defaultValue: 'İletişim & Konum' }), icon: MapPin },
    { id: 3, name: t('profile.professionalInfo', { defaultValue: 'Mesleki Bilgiler' }), icon: Briefcase },
    { id: 4, name: t('profile.pdfCv', { defaultValue: 'PDF CV Yükleme' }), icon: FileText },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90dvh] sm:max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-gray-150">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">{t('profile.wizard.title', { defaultValue: 'CV Güncelleme Sihirbazı' })}</h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('profile.wizard.subtitle', { defaultValue: 'Profilinizi hızlıca güncelleyin' })}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicators */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between overflow-x-auto no-scrollbar">
          {steps.map((s, index) => {
            const Icon = s.icon;
            const isCompleted = step > s.id;
            const isActive = step === s.id;
            return (
              <div key={s.id} className="flex items-center flex-1 last:flex-none min-w-[max-content] sm:min-w-0">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all ${
                    isCompleted ? 'bg-green-500 text-white' :
                    isActive ? 'bg-maritime-600 text-white ring-4 ring-maritime-100' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    {isCompleted ? <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                  </div>
                  <span className={`text-xs sm:text-sm font-medium ${isActive ? 'text-maritime-700 font-semibold' : 'text-gray-500 hidden sm:inline'}`}>
                    {s.name}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 h-0.5 mx-2 sm:mx-4 bg-gray-200 min-w-[20px]" />
                )}
              </div>
            );
          })}
        </div>

        {/* Body */}
        <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-4">
          
          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="space-y-4 animate-in slide-in-from-right duration-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('profile.form.firstName')}</label>
                  <input type="text" name="firstName" className="input" value={formData.firstName} onChange={handleInputChange} />
                </div>
                <div>
                  <label className="label">{t('profile.form.lastName')}</label>
                  <input type="text" name="lastName" className="input" value={formData.lastName} onChange={handleInputChange} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('profile.form.nationality')}</label>
                  <select name="nationality" className="input" value={formData.nationality} onChange={handleInputChange}>
                    <option value="">{t('common:select')}</option>
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">{t('profile.form.dateOfBirth')}</label>
                  <input type="date" name="dateOfBirth" className="input" value={formData.dateOfBirth} onChange={handleInputChange} />
                </div>
              </div>
              <div>
                <label className="label">{t('profile.form.gender')}</label>
                <select name="gender" className="input" value={formData.gender} onChange={handleInputChange}>
                  <option value="">{t('common:select')}</option>
                  <option value="MALE">{t('enums:gender.MALE', { defaultValue: 'Erkek' })}</option>
                  <option value="FEMALE">{t('enums:gender.FEMALE', { defaultValue: 'Kadın' })}</option>
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Contact & Location */}
          {step === 2 && (
            <div className="space-y-4 animate-in slide-in-from-right duration-200">
              <div>
                <label className="label">{t('profile.form.phone')}</label>
                <input type="tel" name="phone" className="input" value={formData.phone} onChange={handleInputChange} placeholder="+90 555 123 45 67" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('profile.form.country')}</label>
                  <select name="country" className="input" value={formData.country} onChange={handleInputChange}>
                    <option value="">{t('common:select')}</option>
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">{t('profile.form.city')}</label>
                  <input type="text" name="city" className="input" value={formData.city} onChange={handleInputChange} placeholder={t('profile.form.enterCity', { defaultValue: 'Şehir giriniz' })} />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Professional Info */}
          {step === 3 && (
            <div className="space-y-4 animate-in slide-in-from-right duration-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('profile.form.department')}</label>
                  <select name="department" className="input" value={formData.department} onChange={handleInputChange}>
                    <option value="">{t('common:select')}</option>
                    <option value="DECK">{t('enums:department.DECK', { defaultValue: 'Güverte' })}</option>
                    <option value="ENGINE">{t('enums:department.ENGINE', { defaultValue: 'Makine' })}</option>
                    <option value="ELECTRICAL">{t('enums:department.ELECTRICAL', { defaultValue: 'Elektrik' })}</option>
                    <option value="CATERING">{t('enums:department.CATERING', { defaultValue: 'Kamara' })}</option>
                  </select>
                </div>
                <div>
                  <label className="label">{t('profile.form.rank')}</label>
                  <SearchableSelect 
                    options={ranks.map((r: any) => ({ value: r.id, label: i18n.language === 'en' ? (r.nameEn || r.nameTr) : (r.nameTr || r.nameEn) }))} 
                    value={formData.rankId} 
                    onChange={(val) => setFormData(prev => ({ ...prev, rankId: val }))} 
                    placeholder={t('common:select')} 
                    searchPlaceholder={t('common:search')} 
                  />
                </div>
              </div>
              <div>
                <label className="label">{t('profile.form.experienceYears')}</label>
                <input type="number" min="0" max="50" name="experienceYears" className="input" value={formData.experienceYears} onChange={handleInputChange} />
              </div>
              <div>
                <label className="label">{t('profile.form.bio')}</label>
                <textarea name="bio" rows={4} className="input" value={formData.bio} onChange={handleInputChange} placeholder={t('profile.form.bioPlaceholder')} />
              </div>
            </div>
          )}

          {/* Step 4: PDF CV Upload */}
          {step === 4 && (
            <div className="space-y-4 animate-in slide-in-from-right duration-200">
              <p className="text-sm text-gray-500">{t('profile.pdfHint')}</p>
              
              {formData.cvPdfUrl ? (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center text-red-600 shrink-0">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{formData.firstName}_{formData.lastName}_CV.pdf</p>
                      <p className="text-xs text-gray-500">{t('common:uploaded')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a href={formData.cvPdfUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary py-1.5 px-3 text-xs hidden sm:inline-flex">
                      {t('profile.viewPdf')}
                    </a>
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, cvPdfUrl: '' }))} className="text-sm text-red-500 hover:text-red-600">
                      {t('common:delete')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {uploadingPdf ? (
                        <>
                          <Loader2 className="w-8 h-8 animate-spin text-maritime-600" />
                          <p className="text-sm text-gray-500 mt-2">{t('profile.uploadingPdf')}</p>
                        </>
                      ) : (
                        <>
                          <FileText className="w-8 h-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500 text-center"><span className="font-semibold">{t('profile.uploadPdf')}</span></p>
                        </>
                      )}
                    </div>
                    <input type="file" accept="application/pdf" className="hidden" onChange={handlePdfUpload} disabled={uploadingPdf} />
                  </label>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 sm:p-6 border-t border-gray-150 bg-gray-50/50 rounded-b-2xl">
          <button 
            type="button" 
            onClick={() => setStep(prev => Math.max(1, prev - 1))}
            className={`btn-secondary ${step === 1 ? 'opacity-0 pointer-events-none' : ''}`}
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common:back', { defaultValue: 'Geri' })}
          </button>
          
          {step < 4 ? (
            <button 
              type="button" 
              onClick={() => setStep(prev => Math.min(4, prev + 1))}
              className="btn-primary"
            >
              {t('common:next', { defaultValue: 'İleri' })}
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button 
              type="button" 
              onClick={handleSave} 
              disabled={loading}
              className="btn-primary"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {t('common:save', { defaultValue: 'Tamamla' })}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
